import { useRef } from 'react';
import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    useFollowCursorBubble,
} from '~/ui-rocco/pages/detail-shared';

/**
 * P6 — "Featured Publication for this Project" (Figma 2354:59890). When a
 * featured article is set in Business Manager, its title sits as a giant
 * editorial statement on the dark background. Hovering it swaps the cursor for a
 * white "Read Article" bubble that eases after the pointer (the dark-mode
 * counterpart of the product page's tagged-projects statement); clicking
 * navigates to the article. Hidden when no featured article is selected.
 */
export const ProjectStorySection = () => {
    const { project } = useLoaderData<typeof loader>();

    // FeaturedArticle is set in BM. The generated GraphQL type lags until
    // `pnpm gql` runs, so read it through a narrow cast for now.
    const featuredArticle = (
        project as typeof project & {
            FeaturedArticle?: { Title: string; Link: string } | null;
        }
    ).FeaturedArticle;

    const eyebrowRef = useRef<HTMLSpanElement | null>(null);
    const titleRef = useRef<HTMLSpanElement | null>(null);
    const { bubbleRef, bubbleVisible, sectionHandlers } = useFollowCursorBubble(
        {
            triggers: [eyebrowRef, titleRef],
        },
    );

    if (!featuredArticle) return null;

    return (
        <section
            {...sectionHandlers}
            className={cn(
                'relative w-full bg-transparent text-white max-md:px-6 md:px-[3rem]',
                // Mirror the product's tagged-projects statement: enlarge the
                // cursor/hover target into the neighbouring spacers without
                // shifting content (each padding cancelled by an equal negative
                // margin), capped within the 4rem/3.5rem gaps to the credits row
                // above and the accordion below so it never overshoots them.
                'md:-mt-[4rem] md:-mb-[3rem] md:pt-[4rem] md:pb-[3rem]',
                // Custom "Read Article" cursor replaces the pointer over the
                // trigger on desktop; mobile keeps the default tap target.
                'md:cursor-none md:[&_a]:cursor-none',
            )}
        >
            <RoccoLink
                to={featuredArticle.Link}
                className={cn(CONTENT_CAP_PADDED, 'block text-left')}
            >
                <span
                    ref={eyebrowRef}
                    className="block w-fit font-sans leading-[1.22] font-medium tracking-[-0.05em] text-[#9a9a9a] max-md:text-sm md:text-[0.9375rem]"
                >
                    Featured Publication for this Project
                </span>
                {/* Giant editorial title (Figma TWK Ghost, 77.8px / white). No
                    line-clamp: its overflow:hidden plus the tight leading-[0.94]
                    clips the cap-tops and descenders. max-width + text-balance
                    keep it to ~two balanced lines, matching the product statement. */}
                <span
                    ref={titleRef}
                    className="mt-[1.7rem] block w-fit max-w-[94.16rem] font-twk-ghost font-normal text-balance text-white max-md:mt-3 max-md:text-[2.25rem] max-md:leading-[1.05] max-md:tracking-[-0.02em] md:text-[4.86rem] md:leading-[0.94] md:tracking-[-0.096rem]"
                >
                    {featuredArticle.Title}
                </span>
            </RoccoLink>

            {/* "Read Article" cursor bubble — eases after the pointer (desktop
                only). The viewport-sized, overflow-hidden wrapper clips it to the
                screen so the bubble (which the rAF can park past an edge) never
                extends the document and adds a scrollbar; the inner node carries
                the eased transform. White circle, dark text for the dark section. */}
            <div
                aria-hidden
                className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
            >
                <div ref={bubbleRef} className="absolute top-0 left-0">
                    <div
                        className={cn(
                            'flex size-[8.875rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white transition-[scale,opacity] duration-300 ease-out',
                            bubbleVisible
                                ? 'scale-100 opacity-100'
                                : 'scale-50 opacity-0',
                        )}
                    >
                        <span className="font-sans leading-[1.3] font-medium tracking-[0.02em] text-[#181818] md:text-[0.916rem]">
                            Read Article
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};
